import { createContext, FC, PropsWithChildren, useContext } from 'react';
import {
  CustomForm,
  FpCompany,
  User,
  useReactComponentsLibraryContext
} from '@flicent/fieldpulse-react-component-library-frontend';

import { CUSTOM_FORM_OBJECT_TYPE_TO_ANALYTICS_RECORD_TYPE } from '@/constants';
import { PendoAnalyticsPlatform, usePendoAnalytics } from '@/hooks';


interface FormAttachedMetadata {
  company: FpCompany;
  user: User;
  form: CustomForm;
}

interface FormSubmittedMetadata {
  isFormAttachedToRecord: boolean;
  company: FpCompany;
  user: User;
  form: CustomForm;
}

interface FormSignedMetadata {
  signerName: string | null;
  company: FpCompany;
  user: User;
  form: CustomForm;
}

interface CustomFormAnalyticsInternalContextValue {
  trackFormAttached: (metadata: FormAttachedMetadata) => void;
  trackFormSubmitted: (metadata: FormSubmittedMetadata) => void;
  trackFormSigned: (metadata: FormSignedMetadata) => void;
}

const CustomFormAnalyticsInternalContext = createContext<CustomFormAnalyticsInternalContextValue | null>(null);

export const useCustomFormAnalyticsInternalContext = (): CustomFormAnalyticsInternalContextValue => {
  const value = useContext(CustomFormAnalyticsInternalContext);

  if (!value) {
    throw new Error('CustomFormAnalyticsInternalContextProvider is missing');
  }

  return value;
};

export const CustomFormAnalyticsInternalContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isInsideMobileApp } = useReactComponentsLibraryContext();
  const { trackEvent } = usePendoAnalytics();

  const platform = isInsideMobileApp ? PendoAnalyticsPlatform.Mobile : PendoAnalyticsPlatform.Web;

  const getInitOptions = (company: FpCompany, user: User) => ({
    account: { id: company.id.toString() },
    visitor: { id: user.id.toString() }
  });

  const trackFormAttached = ({ company, user, form }: FormAttachedMetadata): void => {
    void trackEvent(getInitOptions(company, user), 'Form Attached', {
      form_template_id: form.custom_form_template_id,
      record_type: CUSTOM_FORM_OBJECT_TYPE_TO_ANALYTICS_RECORD_TYPE[form.object_type],
      record_id: form.object_id,
      platform,
    });
  };

  const trackFormSubmitted = ({ isFormAttachedToRecord, form, user, company }: FormSubmittedMetadata): void => {
    void trackEvent(getInitOptions(company, user), 'Form Submitted', {
      form_id: form.id,
      form_template_id: form.custom_form_template_id,
      record_type: isFormAttachedToRecord ? CUSTOM_FORM_OBJECT_TYPE_TO_ANALYTICS_RECORD_TYPE[form.object_type] : null,
      record_id: isFormAttachedToRecord ? form.object_id : null,
      submitted_by: user.id,
      platform,
    });
  };

  const trackFormSigned = ({ signerName, form, user, company }: FormSignedMetadata): void => {
    void trackEvent(getInitOptions(company, user), 'Form Signed', { form_id: form.id, signer: signerName, platform });
  };

  return (
    <CustomFormAnalyticsInternalContext.Provider value={ { trackFormAttached, trackFormSubmitted, trackFormSigned } }>
      { children }
    </CustomFormAnalyticsInternalContext.Provider>
  );
};